<template>
  <div class="image-container" :class="imageContainerClasses" :style="containerStyle">
    <div v-if="!smallPreview" class="header">
      <div class="headline-container">
        <span v-if="showInfo" v-html="infoIcon" class="info"/>
        <h3 class="heading">{{ heading }}</h3>
      </div>

      <input :ref="`edit${heading}`" type="file" :accept="acceptString" @change="onImageFileSelected($event.target.files[0])" hidden>

      <div class="image-actions" v-if="imageFile.url && !smallPreview">
        <button class="button-image-delete" @click="deleteImage">
          <iconComponent symbol="trashcan" stroke="#111" fill="#111"/>
        </button>

        <button class="button-yellow" @click.prevent="$refs['edit' + heading].click()">
          <iconComponent symbol="pencil" stroke="#EA7801" fill="#EA7801"/>
          Edit
        </button>

      </div>
    </div>

    <div v-if="!smallPreview">
      <div v-if="!imageFile.url" class="preview-container" :style="previewSize">
        <input :ref="`fileUpload${heading}`" type="file" :accept="acceptString" @change="onImageFileSelected($event.target.files[0])" hidden>
        <button class="button-yellow" @click.prevent="$refs['fileUpload' + heading].click()">
          <iconComponent symbol="arrowUp" stroke="#EA7801" fill="#EA7801"/>
          Select
        </button>
      </div>
      <div v-else class="preview-container" :style="previewSize" >
        <video v-if="isVideo(imageFile.type)" :src="tmpImageFile.url" style="width: 100%; height: 100%;"></video>
        <img v-else :src="imageFile.url"/>
      </div>
    </div>

    <div v-if="smallPreview" class="small-container" :class="{ 'borderless' : !addImage }">

      <div v-if="addImage" class="add-image" @click="$refs['fileUpload' + heading].click()">
        <input :ref="`fileUpload${heading}`" type="file" :accept="acceptString" @change="onImageFileSelected($event.target.files[0])" hidden>
        <iconComponent symbol="plusMath" size="24px" color="#CCC"/>
        <span>Add image</span>
      </div>

      <div v-if="imageFile.url && !addImage" class="small-preview">
        <video v-if="isVideo(imageFile.type)" :src="tmpImageFile.url" style="width: 100%; height: 100%;"></video>
        <img v-else :src="imageFile.url"/>
      </div>

      <div v-if="!addImage" class="small-image-actions-container">
        <input :ref="`edit${heading}`" type="file" :accept="acceptString" @change="onImageFileSelected($event.target.files[0])" hidden>
        <span style="font-size: 14px; font-weight: 600;">{{ heading }}</span>
        <div class="actions">
          <iconComponent symbol="pencil" :stroke="color" :fill="color" :height="16" :width="16"
                         @click="$refs['edit' + heading].click()" style="cursor: pointer"/>
          <iconComponent symbol="trashcan" stroke="#111" fill="#111" :height="16" :width="16" @click="deleteImage"
                         style="cursor: pointer"/>
        </div>
      </div>
    </div>

    <ul v-if="showAllImageRestrictions && !imageFile.url" class="image-restrictions">
      <li>Supported formats ({{ formats.join(', ') }}).</li>
      <li>File size: Less than {{ size }}MB</li>
      <li v-if="isNaN(aspectWidth) || isNaN(aspectHeight)">Min dimensions: {{ imageDimensions.width }}x{{ imageDimensions.height }}px / Any aspect ratio</li>
      <li v-else>Dimensions: {{ imageDimensions.width }}x{{ imageDimensions.height }}px / Aspect ratio: {{ aspectRatio }}</li>
    </ul>

    <p v-else-if="!showAllImageRestrictions && !smallPreview && !imageFile.url">Supported formats ({{ formats.join(', ') }}).</p>

    <PartnerModal :show="inputState === 'preview-image'" @appeared="loadCropper" @disappeared="destroyCropper" @close="cancelCropping">
      <div class="preview-modal-content">
        <div class="preview-modal-image-container" >
          <img :id="cropperId" :src="tmpImageFile.url"/>
        </div>
      </div>

      <div class="preview-modal-footer">
        <button class="button-yellow-outlined" @click="changeImage">
          <iconComponent symbol="arrowUp" stroke="#EA7801" fill="#EA7801" />
          Upload new
        </button>
        <button class="button-solid" @click="selectImage">
          <iconComponent symbol="floppyDisk" stroke="#FFFFFF" fill="#FFFFFF" />
          Crop and save
        </button>
      </div>
    </PartnerModal>

    <PartnerModal :show="inputState === 'preview-video'" @close="cancelCropping">
      <div class="preview-modal-content">
        <div class="preview-modal-image-container" >
          <video controls :src="tmpImageFile.url" style="width: 100%; height: 100%;"></video>
        </div>
      </div>

      <div class="preview-modal-footer">
        <button class="button-yellow-outlined" @click="changeImage">
          <iconComponent symbol="arrowUp" stroke="#EA7801" fill="#EA7801" />
          Upload new
        </button>
        <button class="button-solid" @click="selectImage">
          <iconComponent symbol="floppyDisk" stroke="#FFFFFF" fill="#FFFFFF" />
          Save
        </button>
      </div>
    </PartnerModal>
  </div>
</template>

<script>
import Cropper from 'cropperjs';
import PartnerModal from '../PartnerModal/PartnerModal.vue'
import svgMixin from "@/mixins/SvgMixin.vue";

export default {
  name: 'ImageSelect',
  components: { PartnerModal },
  mixins: [svgMixin],
  props: {
    heading: {
      type: String,
      default: '',
    },
    selectedImageFile: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Boolean,
      default: true,
    },
    showAllImageRestrictions: {
      type: Boolean,
      default: true,
    },
    aspectRatio: {
      type: String,
      default: '16:9',
    },
    dimensions: {
      type: String,
    },
    formats: {
      type: Array,
      default: () => ['jpg', 'jpeg', 'png']
    },
    size: {
      type: Number,
      default: 4,
    },
    showInfo: {
      type: Boolean,
      default: false,
    },
    smallPreview: {
      type: Boolean,
      default: false,
    },
    addImage: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Number,
      default: 570,
    },
    height: {
      type: Number,
      default: 310,
    },
  },
  data() {
    return {
      imageFile: {},
      cropper: null,
      inputState: 'select',
      showModal: false,
      tmpImageFile: {},
      tmpImageProperties: {},
    };
  },
  mounted() {
    if (!this.selectedImageFile?.fileObject && !this.selectedImageFile?.url) {
      return
    }

    this.inputState = 'selected'

    if (this.selectedImageFile?.fileObject) {
      this.imageFile.url = this.selectedImageFile.fileObject.url;
    } else {
      this.imageFile.url = this.selectedImageFile.url;
    }
  },
  unmounted() {
    this.destroyCropper();
  },
  computed: {
    acceptString() {
      const mimeMap = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        mp4: 'video/mp4',
        mov: 'video/quicktime'
      };
      return this.formats
        .map(f => mimeMap[f.toLowerCase()] || `image/${f}`)
        .join(',');
    },
    imageContainerClasses() {
      return {
        'small': this.smallPreview,
        'image--selected': this.selected,
      }
    },
    aspectWidth() {
      if (typeof this.aspectRatio === 'string' && this.aspectRatio.includes(':')) {
        return parseInt(this.aspectRatio.split(':')[0]);
      }
      return 1;
    },
    aspectHeight() {
      if (typeof this.aspectRatio === 'string' && this.aspectRatio.includes(':')) {
        return parseInt(this.aspectRatio.split(':')[1]);
      }
      return 1;
    },
    imageDimensions() {
      if (typeof this.dimensions === 'string' && this.dimensions.includes('x')) {
        const [width, height] = this.dimensions.split('x').map(dimension => parseInt(dimension));
        return { width, height };
      }

      const minDimension = 1080;
      if (isNaN(this.aspectHeight) || isNaN(this.aspectWidth)) {
        return { width: minDimension, height: minDimension };
      }
      const [width, height] = this.aspectWidth < this.aspectHeight
        ? [minDimension, minDimension / this.aspectWidth * this.aspectHeight]
        : [minDimension / this.aspectHeight * this.aspectWidth, minDimension];

      return { width, height };
    },
    cropperId() {
      return `cropper-${this.heading.toLowerCase().replace(/ /g, "-")}`;
    },
    color() {
      return this.smallPreview ? '#111111' : '#EA7801';
    },
    containerStyle() {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`,
      };
    },
    previewSize() {
      let width = this.width - 32;
      let height = this.height * 0.7;

      if (this.imageFile?.url === undefined) {
        height = this.height * 0.4;
      } else {
        height = '';
      }

      return {
        width: `${width}px`,
        height: `${height}px`,
      }
    },
  },
  methods: {
    getImageProperties(imageFile) {
      return new Promise((resolve, reject) => {
        if (!imageFile) {
          reject('No image');
        }

        const URL = window.URL || window.webkitURL;
        if (!URL?.createObjectURL) {
          reject('No URL object')
        }

        const img = new Image();
        img.onload = () => {
          const fileSizeInBytes = imageFile.size;
          const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
          resolve({ width: img.width, height: img.height, size: fileSizeInMB });
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(imageFile);
      });
    },
    async onImageFileSelected(imageFile) {
      if (!imageFile) {
        return;
      }

      const validFormats = this.formats.map(format => format.toLowerCase());
      if (!validFormats.includes(imageFile.name.split('.').pop().toLowerCase())) {
        this.setAlert('The selected file is not in a supported file format', 3, 'danger');
        return;
      }

      try {
        this.tmpImageProperties = await this.getImageProperties(imageFile);
      } catch (error) {
        // console.log('Failed to read image dimensions', error);
      }

      /*
      dont use cropper for videos and if no aspect ratio is set
      if (!this.isVideo(imageFile.type) && isNaN(this.aspectWidth) && isNaN(this.aspectHeight)) {
        this.selectImageWithoutCropping(imageFile);
        return;
      }*/

      this.openImageCropper(imageFile);
    },
    selectImageWithoutCropping(imageFile) {
      this.imageFile = imageFile;

      let URL = window.URL || window.webkitURL;
      if (URL && URL.createObjectURL) {
        this.imageFile.url = URL.createObjectURL(imageFile);
      }
      this.inputState = 'selected';
      this.$emit('imageSelected', { fileObject: this.imageFile });
    },
    openImageCropper(imageFile) {
      this.tmpImageFile = imageFile;

      let URL = window.URL || window.webkitURL;
      if (URL && URL.createObjectURL) {
        this.tmpImageFile.url = URL.createObjectURL(imageFile);
      }

      if (this.isVideo(this.tmpImageFile.type)) {
        this.inputState = 'preview-video';
      } else {
        this.inputState = 'preview-image';
      }
    },
    isVideo(type) {
      if (!type) return false;
      return type.includes('video');
    },
    loadCropper() {
      this.$nextTick(function () {
        const image = document.getElementById(this.cropperId);

        if (!image) {
          return;
        }

        this.cropper = new Cropper(image);

        const canvasEl = this.cropper.getCropperCanvas();
        const selectionEl = this.cropper.getCropperSelection();
        const cropperImage = this.cropper.getCropperImage();

        selectionEl.outlined = true;

        const ratio = this.aspectWidth / this.aspectHeight;
        const hasFixedRatio = Number.isFinite(ratio) && ratio > 0;
        if (hasFixedRatio) {
          // Handles stay active but resizing is constrained to this ratio
          selectionEl.aspectRatio = ratio;
        }

        // The canvas is a fixed 800x500 box (see CSS below), but the image is
        // scaled to fit and centered inside it, so it rarely fills the canvas
        // on both axes. Bound everything to the image's own rendered box
        // instead of the canvas's, so the selection can't stray into that
        // surrounding empty space. The image never moves once loaded, so its
        // bounds are measured once and cached — re-measuring via
        // getBoundingClientRect() on every drag event returns subtly
        // different subpixel values each call, which made the clamp below
        // recurse without ever settling and blew the call stack.
        let imageBounds = null;

        // Default the selection to the full image instead of the library's
        // own initial-coverage, which sizes/positions relative to the canvas
        cropperImage.$ready(() => {
          const canvasRect = canvasEl.getBoundingClientRect();
          const imageRect = cropperImage.getBoundingClientRect();
          imageBounds = {
            left: imageRect.left - canvasRect.left,
            top: imageRect.top - canvasRect.top,
            width: imageRect.width,
            height: imageRect.height,
          };

          const { left, top, width, height } = imageBounds;
          let selectionWidth = width;
          let selectionHeight = height;
          if (hasFixedRatio) {
            if (ratio > width / height) {
              selectionHeight = width / ratio;
            } else {
              selectionWidth = height * ratio;
            }
          }
          selectionEl.$change(
            left + (width - selectionWidth) / 2,
            top + (height - selectionHeight) / 2,
            selectionWidth,
            selectionHeight,
          );
        });

        // Disable ability to go outside of the image boundaries when resizing/moving the selection.
        // $change() rounds to whole pixels and re-applies the aspect-ratio "cover" adjustment
        // internally, which can nudge a value we just corrected right back out of tolerance. Ignore
        // sub-pixel differences and block re-entrant correction, so that dance can't loop forever.
        let correctingSelection = false;
        selectionEl.addEventListener('change', (event) => {
          if (!imageBounds || correctingSelection) {
            return;
          }

          const { x, y, width, height } = event.detail;
          const { left, top, width: imageWidth, height: imageHeight } = imageBounds;

          let clampedWidth = width;
          let clampedHeight = height;

          if (hasFixedRatio) {
            // Locked ratio: both dimensions must shrink together, or the ratio breaks
            const scale = Math.min(1, imageWidth / width, imageHeight / height);
            clampedWidth = width * scale;
            clampedHeight = height * scale;
          } else {
            // Free resize: each axis is independent, only clamp the one that overflowed
            clampedWidth = Math.min(width, imageWidth);
            clampedHeight = Math.min(height, imageHeight);
          }

          const clampedX = Math.max(left, Math.min(x, left + imageWidth - clampedWidth));
          const clampedY = Math.max(top, Math.min(y, top + imageHeight - clampedHeight));

          const TOLERANCE = 0.5;
          if (Math.abs(clampedX - x) > TOLERANCE || Math.abs(clampedY - y) > TOLERANCE
            || Math.abs(clampedWidth - width) > TOLERANCE || Math.abs(clampedHeight - height) > TOLERANCE) {
            event.preventDefault();
            correctingSelection = true;
            selectionEl.$change(clampedX, clampedY, clampedWidth, clampedHeight);
            correctingSelection = false;
          }
        });
      });
    },
    async cropImage() {
      if (this.cropper == null) {
        this.setAlert('The file could not be cropped', 3, 'danger');
        return false;
      }

      const selection = this.cropper.getCropperSelection();
      if (!selection) {
        this.setAlert('The file could not be cropped', 3, 'danger');
        return false;
      }

      // Without explicit dimensions, $toCanvas() renders at the on-screen
      // (CSS pixel) size of the selection, which is smaller than the
      // source image's natural resolution since the preview is scaled
      // down to fit the modal. Scale the output back up by the inverse
      // of that display scale so the crop keeps the original resolution.
      const cropperImage = this.cropper.getCropperImage();
      const [displayScale] = cropperImage.$getTransform();
      const outputScale = displayScale > 0 ? 1 / displayScale : 1;

      const canvas = await selection.$toCanvas({
        width: selection.width * outputScale,
        height: selection.height * outputScale,
      });
      const dataUrl = canvas.toDataURL(this.tmpImageFile.type);
      const binStr = atob(dataUrl.split(',')[1]);
      const arr = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) {
        arr[i] = binStr.charCodeAt(i);
      }
      this.imageFile = new File([arr], this.generateFilename(), { type: this.tmpImageFile.type });
      return true;
    },
    async selectImage() {
      if (this.isVideo(this.tmpImageFile.type)) {
        this.imageFile = this.tmpImageFile;
      } else if (!(await this.cropImage())) {
        return;
      }

      let URL = window.URL || window.webkitURL;
      if (URL && URL.createObjectURL) {
        this.imageFile.url = URL.createObjectURL(this.imageFile);
      }
      this.inputState = 'selected';
      this.$emit("imageSelected", { fileObject: this.imageFile });
    },
    changeImage() {
      this.inputState = 'select';
      setTimeout(() => {
        this.$refs['edit' + this.heading].click();
      }, 400);
    },
    cancelCropping() {
      this.inputState = 'select';
    },
    destroyCropper() {
      if (this.cropper !== null) {
        this.cropper.destroy();
        this.cropper = null;
      }

      if (this.tmpImageFile) {
        this.tmpImageFile = {};
        this.tmpImageProperties = {};
      }
    },
    getExtension() {
      if (!this.tmpImageFile?.name) return ''
      return this.tmpImageFile.name.split('.').pop();
    },
    generateFilename() {
      const extension = this.getExtension();
      const name = this.tmpImageFile.name.replace(`.${extension}`,'')
      return `${name}${Date.now()}.${extension}`
    },
    deleteImage() {
      this.imageFile = {};
      this.inputState = 'select';

      this.$emit("imageDeleted");
    }
  },
}
</script>

<style lang="scss" scoped>
@use "../../../assets/scss/components/company";
@use "../../../assets/scss/abstracts/_colors.scss";

.image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 441px;
  padding: 16px;

  border: 1px dashed #CCC;

  &.small {
    justify-content: center;
    width: 130px;
    height: 145px;
    padding: 0;

    border: none;
    background-color: #FFF;
  }

  .small-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 130px;
    height: 145px;
    padding: 0;
    border: 1px dashed #CCC;
    border-radius: 8px;
    background-color: #FFF;

    &.borderless {
      border: none;
    }

    .add-image {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      gap: 4px;
      cursor: pointer;
    }

    .small-preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      height: 110px;
      padding: 8px 16px;
      border-radius: 8px 8px 0 0;
      background-color: #F2F2F2;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;

        border-radius: 8px;
      }
    }

    .small-image-actions-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 36px;
      padding: 0 12px;
      background: #FFF;
      border: 1px solid #CCC;
      border-radius: 0 0 8px 8px;

      .actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

  }

  .dashed {
    border: 1px dashed #CCC;
  }

  .header {
    display: flex;
    align-items: center;

    justify-content: space-between;
    width: 100%;
    height: 32px;
    margin-bottom: 16px;

    .headline-container {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      gap: 4px;

      .info {
        cursor: pointer;
      }

      .heading {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;
        line-height: 22.4px;
        letter-spacing: normal;
        color: #111;
      }
    }

    .name--selected {
      font-weight: 700;
    }
    .button-yellow {
      min-height: 30px;
      border: 2px solid $brand-yellow;
    }
  }

  .preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    // width: 540px;
    // height: 230px;
    border-radius: 4px;
    background-color: #F2F2F2;

    &.empty {
      height: 180px;
    }

    &.small-preview {
      height: 145px;
      padding: 8px 16px;
    }

    button {
      background-color: #FFF;
      border: 2px solid $brand-yellow;
    }

    .preview-container-buttons {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: flex-end;
      z-index: 1000;

      .button-cancel {
        background-color: transparent;
        color: #333;
        border: 1px solid #333;
        margin-right: 5px;
        border-radius: 3px;
      }

      .button-apply {
        background-color: $brand-yellow;
        color: white;
        margin-right: 5px;
        border-radius: 3px;
      }
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .image-restrictions {
    width: 100%;
    margin: 8px 0 0 0;
    padding: 0;
    list-style-type: disc;
    list-style-position: inside;

      li {
        padding: 0 0 0 8px;
        font-size: 14px;
        font-weight: 400;
        line-height: 19.6px;
        letter-spacing: 0.014px;
        text-align: left;
        color: #666;
      }
  }

  p {
    align-self: start;
    margin-top: 8px;
  }
}

.image--selected {
  border-color: #111;
}

  .preview-modal-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    padding: 64px 128px;

    .preview-modal-image-container {
      position: relative;
      width: 800px;
      height: 500px;

      img {
        width: 100%;
      }

      :deep(cropper-canvas) {
        position: absolute;
        inset: 0;
      }

      :deep(cropper-selection) {
        outline: 2px solid #EA7801;
      }
    }
  }

  .preview-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    padding: 24px 40px;
    height: 88px;
    background-color: #F2F2F2;
  }

.image-actions {
  display: flex;
  justify-items: flex-end;
  align-items: center;
}

.small-delete-preview {
  padding: 0 12px 0 8px;
}


</style>
